import { useCallback, useEffect, useState } from 'react';

import {
  listAffiliates, createAffiliate, updateAffiliate, listAffiliateContracts,
  createAffiliateContract, type AdminAffiliate, type AffiliateContract,
  type ThresholdScope,
} from '../../lib/affiliates';
import { listEvents, type EventCardData } from '../../lib/catalog';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort, formatMoney } from '../../lib/format';
import './AdminPages.css';

/**
 * Affiliates: a flat commission per attributed ticket.
 *
 * NOT AN INVESTOR SCREEN. An affiliate risks no capital and holds no shares, so nothing
 * here has a hurdle, a carry or a share count. What they are owed is a multiplication.
 *
 * THE TWO TERMS THAT DECIDE THE MONEY are both real, editable fields on this form, and
 * that is the point of the screen rather than an implementation detail:
 *
 *   Commission starts after   any number, including 0 for "every ticket earns". It is
 *                             EXCLUSIVE, so 25 means the 26th attributed ticket is the
 *                             first that pays. The form says so next to the input, because
 *                             off-by-one here is a real invoice dispute.
 *
 *   Threshold scope           lifetime or per event, chosen explicitly. On identical sales
 *                             the two pay different amounts, so the form shows what the
 *                             current choice means rather than leaving it to be inferred
 *                             from the label.
 *
 * THE TRACKING CODE IS NOT EDITABLE and the form says why: it is in every link already
 * printed, emailed and embedded, and changing it strands all of them with no error
 * anywhere. The engine refuses the write too.
 */

const SCOPES: { value: ThresholdScope; label: string; explains: string }[] = [
  {
    value: 'lifetime',
    label: 'Lifetime (all linked events)',
    explains: 'One running count across every event this affiliate is linked to. They clear '
      + 'the threshold once, ever.',
  },
  {
    value: 'per_event',
    label: 'Per event',
    explains: 'The count restarts for each event, so they clear the threshold again on every '
      + 'show. This pays the affiliate less on the same sales.',
  },
];

const EMPTY = {
  title: '',
  baseUrl: '',
  commissionDollars: '',
  commissionStartsAfter: '0',
  thresholdScope: 'lifetime' as ThresholdScope,
  backgroundVideoUrl: '',
  backgroundVideoPosterUrl: '',
};

export function AdminAffiliates() {
  const [rows, setRows] = useState<AdminAffiliate[] | null>(null);
  const [events, setEvents] = useState<EventCardData[]>([]);
  const [contracts, setContracts] = useState<Record<string, AffiliateContract[]>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([listAffiliates(), listEvents({}).catch(() => [])])
      .then(([a, e]) => { setRows(a); setEvents(Array.isArray(e) ? e : []); })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load affiliates.');
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function resetForm() { setForm({ ...EMPTY }); setEditingId(null); }

  function startEdit(a: AdminAffiliate) {
    setEditingId(a.id);
    setForm({
      title: a.title,
      baseUrl: a.baseUrl,
      commissionDollars: (a.commissionPerTicketCents / 100).toFixed(2),
      commissionStartsAfter: String(a.commissionStartsAfter),
      thresholdScope: a.thresholdScope,
      backgroundVideoUrl: a.backgroundVideoUrl ?? '',
      backgroundVideoPosterUrl: a.backgroundVideoPosterUrl ?? '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);

    const dollars = Number(form.commissionDollars);
    if (!Number.isFinite(dollars) || dollars < 0) {
      setError('Commission per ticket must be a number, 0 or more.'); return;
    }
    // Parsed strictly. An empty threshold is not zero, it is a field nobody filled in, and
    // silently reading it as zero would start paying commission on ticket one.
    const threshold = Number.parseInt(form.commissionStartsAfter, 10);
    if (!Number.isInteger(threshold) || threshold < 0) {
      setError('Commission starts after must be a whole number, 0 or more.'); return;
    }

    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        baseUrl: form.baseUrl.trim(),
        commissionPerTicketCents: Math.round(dollars * 100),
        commissionStartsAfter: threshold,
        thresholdScope: form.thresholdScope,
        backgroundVideoUrl: form.backgroundVideoUrl.trim() || null,
        backgroundVideoPosterUrl: form.backgroundVideoPosterUrl.trim() || null,
      };
      if (editingId) {
        const updated = await updateAffiliate(editingId, payload);
        setRows((cur) => cur?.map((r) => (r.id === editingId ? { ...r, ...updated } : r)) ?? cur);
        setNotice(`Updated ${updated.title}.`);
      } else {
        const created = await createAffiliate(payload);
        setRows((cur) => [{ ...created, contractCount: 0 }, ...(cur ?? [])]);
        setNotice(`Created ${created.title}. Link it to an event below to get a contract number.`);
      }
      resetForm();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not save that affiliate.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleContracts(a: AdminAffiliate) {
    if (expanded === a.id) { setExpanded(null); return; }
    setExpanded(a.id);
    if (contracts[a.id]) return;
    try {
      const list = await listAffiliateContracts(a.id);
      setContracts((c) => ({ ...c, [a.id]: list }));
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not load contracts.');
    }
  }

  async function linkEvent(a: AdminAffiliate, eventId: string) {
    if (!eventId) return;
    setBusy(true);
    setError(null);
    try {
      await createAffiliateContract(a.id, eventId);
      const list = await listAffiliateContracts(a.id);
      setContracts((c) => ({ ...c, [a.id]: list }));
      setRows((cur) => cur?.map((r) => (
        r.id === a.id ? { ...r, contractCount: list.length } : r)) ?? cur);
      setNotice('Contract created. The number below is what the affiliate types in to see their dashboard.');
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not link that event.');
    } finally {
      setBusy(false);
    }
  }

  async function copy(text: string, tag: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard access is refused in some contexts. Both values are on screen and
      // selectable, so this is a convenience failing rather than the feature failing.
      setCopied(null);
    }
  }

  const scope = SCOPES.find((s) => s.value === form.thresholdScope)!;
  const threshold = Number.parseInt(form.commissionStartsAfter, 10);

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Affiliates</h1>
          <p className="adm-lede">
            A flat commission per attributed ticket. An affiliate holds no shares and takes
            no part in any investor waterfall.
          </p>
        </div>
        {rows && (
          <div className="adm-stat">
            <span className="adm-stat__value mono">{rows.length}</span>
            <span className="adm-stat__label">affiliates</span>
          </div>
        )}
      </header>

      {error && <p className="adm-status adm-error" role="alert">{error}</p>}
      {notice && <p className="adm-status" role="status">{notice}</p>}

      {/* ── Create / edit ─────────────────────────────────────────────────── */}
      <section className="adm-card">
        <header className="adm-card__head">
          <h2 className="adm-card__title">
            {editingId ? 'Edit affiliate' : 'New affiliate'}
          </h2>
          {editingId && (
            <button type="button" className="button button--ghost adm-btn-sm" onClick={resetForm}>
              Cancel edit
            </button>
          )}
        </header>

        <form className="adm-form" onSubmit={submit}>
          <label className="field">
            <span className="field__label">Affiliate or show name *</span>
            <input className="field__input" type="text" required value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>

          <label className="field">
            <span className="field__label">Base URL *</span>
            <input className="field__input" type="url" required value={form.baseUrl}
              placeholder="https://..."
              onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} />
            <span className="adm-hint">
              Where the tracking link points. The tracking code is appended automatically,
              so the link is generated and never typed.
            </span>
          </label>

          <label className="field">
            <span className="field__label">Commission per ticket ($) *</span>
            <input className="field__input mono" type="number" min="0" step="0.01" required
              value={form.commissionDollars}
              onChange={(e) => setForm({ ...form, commissionDollars: e.target.value })} />
          </label>

          {/* THE THRESHOLD. A real number field, any value, including 0. */}
          <label className="field">
            <span className="field__label">Commission starts after *</span>
            <input className="field__input mono" type="number" min="0" step="1" required
              value={form.commissionStartsAfter}
              onChange={(e) => setForm({ ...form, commissionStartsAfter: e.target.value })} />
            <span className="adm-hint">
              Counted in tickets, not orders, and exclusive.{' '}
              {Number.isInteger(threshold) && threshold > 0
                ? `Ticket ${threshold + 1} is the first that earns; the first ${threshold} pay nothing.`
                : 'Set to 0, so every attributed ticket earns from the first one.'}
            </span>
          </label>

          {/* THE SCOPE. Visible, selectable, and it says what it does. */}
          <label className="field">
            <span className="field__label">Threshold scope *</span>
            <select className="adm-select" value={form.thresholdScope}
              onChange={(e) => setForm({ ...form, thresholdScope: e.target.value as ThresholdScope })}>
              {SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <span className="adm-hint">{scope.explains}</span>
          </label>

          <label className="field">
            <span className="field__label">Background video URL (optional)</span>
            <input className="field__input" type="url" value={form.backgroundVideoUrl}
              placeholder="https://... .mp4"
              onChange={(e) => setForm({ ...form, backgroundVideoUrl: e.target.value })} />
          </label>

          <label className="field">
            <span className="field__label">Video poster image (optional)</span>
            <input className="field__input" type="url" value={form.backgroundVideoPosterUrl}
              placeholder="https://... .jpg"
              onChange={(e) => setForm({ ...form, backgroundVideoPosterUrl: e.target.value })} />
            <span className="adm-hint">
              Shown wherever autoplay is refused, which is common on phones. Worth setting
              whenever a video is.
            </span>
          </label>

          <div className="adm-card__actions">
            <button type="submit" className="button button--primary adm-btn-sm" disabled={busy}>
              {busy ? 'Saving…' : editingId ? 'Save changes' : 'Create affiliate'}
            </button>
          </div>
        </form>
      </section>

      {loading && <p className="adm-status">Loading…</p>}
      {!loading && rows?.length === 0 && (
        <p className="adm-status adm-muted">No affiliates yet.</p>
      )}

      {/* ── The list ──────────────────────────────────────────────────────── */}
      <div className="adm-list">
        {rows?.map((a) => (
          <article key={a.id} className="adm-card">
            <header className="adm-card__head">
              <div>
                <h2 className="adm-card__title">{a.title}</h2>
                <p className="adm-card__meta">
                  <span className="mono">{a.code}</span>
                  {' · '}{formatMoney(a.commissionPerTicketCents)} per ticket
                  {' · '}
                  {a.commissionStartsAfter > 0
                    ? `starts on ticket ${a.commissionStartsAfter + 1}`
                    : 'every ticket earns'}
                  {' · '}{a.thresholdScope === 'lifetime' ? 'lifetime' : 'per event'}
                  {' · '}created {formatDateShort(a.createdAt)}
                </p>
              </div>
              <span className={`adm-pill adm-pill--${a.status === 'active' ? 'approved' : 'pending'}`}>
                {a.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </header>

            <div className="adm-card__body">
              {/* The tracking link, ready to hand over. */}
              {a.trackingLink && (
                <dl className="adm-metrics">
                  <div>
                    <dt>Tracking link</dt>
                    <dd>
                      <code className="adm-json">{a.trackingLink}</code>
                      <button type="button" className="button button--ghost adm-btn-sm"
                        onClick={() => copy(a.trackingLink!, `link-${a.id}`)}>
                        {copied === `link-${a.id}` ? 'Copied' : 'Copy link'}
                      </button>
                    </dd>
                  </div>
                </dl>
              )}

              <div className="adm-card__actions">
                <button type="button" className="button button--ghost adm-btn-sm"
                  onClick={() => startEdit(a)}>Edit terms</button>
                <button type="button" className="button button--ghost adm-btn-sm"
                  onClick={() => toggleContracts(a)}>
                  {expanded === a.id ? 'Hide contracts' : `Contracts (${a.contractCount})`}
                </button>
                <button type="button" className="button button--ghost adm-btn-sm"
                  disabled={busy}
                  onClick={() => updateAffiliate(a.id, {
                    status: a.status === 'active' ? 'inactive' : 'active',
                  }).then((u) => setRows((cur) => cur?.map((r) => (
                    r.id === a.id ? { ...r, status: u.status } : r)) ?? cur))
                    .catch(() => setError('Could not change that status.'))}>
                  {a.status === 'active' ? 'Deactivate' : 'Reactivate'}
                </button>
              </div>

              {expanded === a.id && (
                <div className="adm-derived">
                  <h3 className="adm-card__title">Contract numbers</h3>
                  <p className="adm-hint">
                    This is the credential the affiliate types in at /affiliates to see their
                    dashboard. One per event.
                  </p>

                  {(contracts[a.id] ?? []).length === 0 && (
                    <p className="adm-muted">No events linked yet.</p>
                  )}

                  {(contracts[a.id] ?? []).map((c) => (
                    <dl key={c.id} className="adm-metrics">
                      <div>
                        <dt>{c.event.title}</dt>
                        <dd>
                          <code className="adm-json mono">{c.contractNumber}</code>
                          <button type="button" className="button button--ghost adm-btn-sm"
                            onClick={() => copy(c.contractNumber, `ct-${c.id}`)}>
                            {copied === `ct-${c.id}` ? 'Copied' : 'Copy number'}
                          </button>
                        </dd>
                      </div>
                    </dl>
                  ))}

                  <label className="field">
                    <span className="field__label">Link another event</span>
                    <select className="adm-select" defaultValue=""
                      disabled={busy}
                      onChange={(e) => { linkEvent(a, e.target.value); e.target.value = ''; }}>
                      <option value="">Choose an event…</option>
                      {events
                        .filter((ev) => !(contracts[a.id] ?? []).some((c) => c.event.id === ev.id))
                        .map((ev) => (
                          <option key={ev.id} value={ev.id}>{ev.title}</option>
                        ))}
                    </select>
                  </label>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
